import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Clock, Inbox, ListChecks, Users } from "lucide-react";

import { api } from "../services/api";
import { getAll } from "../services/pagination";
import { academicSetupService } from "../services/academicSetupService";
import { timetableService, type TimetableSlot } from "../services/timetableService";
import { GeneratePanel } from "../components/timetable/GeneratePanel";
import { LessonsPanel } from "../components/timetable/LessonsPanel";
import { RequestsPanel } from "../components/timetable/RequestsPanel";
import { AssignmentsPanel, PeriodsPanel, type Option, type Teacher } from "../components/timetable/SetupPanels";
import { classLevels, withLessonCounts, type Assignment } from "../components/timetable/timetableLogic";

type Tab = "lessons" | "periods" | "assignments" | "generate" | "requests";
const TABS: { id: Tab; label: string; icon: typeof Clock }[] = [
  { id: "lessons", label: "Timetable", icon: CalendarClock },
  { id: "periods", label: "Periods", icon: Clock },
  { id: "assignments", label: "Teaching assignments", icon: Users },
  { id: "generate", label: "Generate", icon: ListChecks },
  { id: "requests", label: "Change requests", icon: Inbox },
];

/** The Director of Studies' timetable: set up the day and who teaches what, generate, and decide teachers' change requests. */
export default function TimetableManagement() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.some((item) => item.id === params.get("tab")) ? (params.get("tab") as Tab) : "lessons";
  const [yearId, setYearId] = useState<number | null>(null);

  const years = useQuery({ queryKey: ["timetable-years"], queryFn: academicSetupService.getYears });
  useEffect(() => {
    if (!yearId && years.data?.length) setYearId((years.data.find((item) => item.is_current) ?? years.data[0]).id);
  }, [years.data, yearId]);
  const year = years.data?.find((item) => item.id === yearId);
  const yearName = year?.name ?? "";

  const classes = useQuery({ queryKey: ["timetable-classes", yearId], queryFn: () => getAll<Option>("/students/classes/", { academic_year: yearId }), enabled: Boolean(yearId) });
  const subjects = useQuery({ queryKey: ["timetable-subjects"], queryFn: () => getAll<Option>("/examination/subjects/") });
  const teachers = useQuery({ queryKey: ["timetable-teachers"], queryFn: async () => (await api.get("/academics/dos/teachers/available/")).data as Teacher[] });
  const assignments = useQuery({ queryKey: ["timetable-assignments", yearId], queryFn: () => getAll<Assignment>("/academics/teacher/teacher-assignments/", { academic_year: yearId }), enabled: Boolean(yearId) });
  const periods = useQuery({ queryKey: ["timetable-periods", yearId], queryFn: () => timetableService.getPeriods(yearId!), enabled: Boolean(yearId) });
  const slots = useQuery({ queryKey: ["timetable-slots", yearId], queryFn: () => getAll<TimetableSlot>("/timetable/slots/", { academic_year_id: yearId }), enabled: Boolean(yearId) });
  const generations = useQuery({ queryKey: ["timetable-generations"], queryFn: timetableService.getGenerations });
  const requests = useQuery({ queryKey: ["timetable-requests"], queryFn: () => timetableService.getRequests() });
  const refresh = () =>
    Promise.all(
      ["timetable-periods", "timetable-period", "timetable-assignments", "timetable-slots", "timetable-generations", "timetable-requests", "academic-year-planner", "my-timetable"].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      ),
    );

  const periodList = useMemo(() => periods.data ?? [], [periods.data]);
  const periodMap = useMemo(() => new Map(periodList.map((period) => [period.id, period])), [periodList]);
  const yearRequests = requests.data?.filter((item) => !yearId || item.academic_year === yearId);
  const waiting = yearRequests?.filter((item) => item.status === "pending" || item.status === "approved").length ?? 0;
  const counts: Partial<Record<Tab, number | undefined>> = {
    lessons: slots.data?.length,
    periods: periods.data?.length,
    assignments: assignments.data?.length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Timetable</h1>
          <p className="mt-1 text-sm text-gray-500">The school day, who teaches what, the generated week, and teachers' requests to change it.</p>
        </div>
        <label className="filter-field min-w-56">
          Academic year
          <select className="input-compact" value={yearId ?? ""} onChange={(event) => setYearId(Number(event.target.value))}>
            {years.data?.map((item) => <option key={item.id} value={item.id}>{item.name}{item.is_current ? " (current)" : ""}</option>)}
          </select>
        </label>
      </div>

      <nav role="tablist" aria-label="Timetable sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {TABS.map((item) => {
          const active = tab === item.id;
          const badge = item.id === "requests" ? waiting || undefined : counts[item.id];
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setParams(item.id === "lessons" ? {} : { tab: item.id }, { replace: true })}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${
                active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {badge !== undefined && (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-xs tabular-nums ${
                    item.id === "requests" ? "bg-amber-100 text-amber-800" : active ? "bg-primary-100 text-primary-700" : "bg-gray-100 text-gray-600"
                  }`}
                  title={item.id === "requests" ? "Waiting for a decision or to be made" : undefined}
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {tab === "lessons" && (
        <LessonsPanel
          yearName={yearName}
          slots={slots.data}
          periods={periodList}
          classes={classes.data ?? []}
          teachers={teachers.data ?? []}
          isLoading={slots.isLoading || !yearId}
          isError={slots.isError}
          onRetry={() => slots.refetch()}
        />
      )}
      {tab === "periods" && (
        <PeriodsPanel
          yearId={yearId}
          yearName={yearName}
          rows={withLessonCounts(periods.data, slots.data)}
          levels={classLevels(classes.data)}
          isLoading={periods.isLoading || !yearId}
          canWrite
          onChanged={refresh}
        />
      )}
      {tab === "assignments" && (
        <AssignmentsPanel
          yearId={yearId}
          yearName={yearName}
          rows={assignments.data}
          isLoading={assignments.isLoading || !yearId}
          teachers={teachers.data ?? []}
          subjects={subjects.data ?? []}
          classes={classes.data ?? []}
          canWrite
          onChanged={refresh}
        />
      )}
      {tab === "generate" && (
        <GeneratePanel
          yearId={yearId}
          yearName={yearName}
          periods={periodList}
          assignments={assignments.data ?? []}
          slots={slots.data ?? []}
          generations={generations.data}
          isLoading={generations.isLoading}
          canWrite
          onChanged={refresh}
        />
      )}
      {tab === "requests" && (
        <RequestsPanel
          title={`Change requests · ${yearName}`}
          description="Teachers ask for a lesson to move, change room or be removed. Decide, then make the change."
          rows={yearRequests}
          periods={periodMap}
          isLoading={requests.isLoading}
          isError={requests.isError}
          onRetry={() => requests.refetch()}
          canDecide
          onChanged={refresh}
        />
      )}
    </div>
  );
}
